import { useEffect, useState } from "react";
import Footer from "../globalComponents/footer";

const apiBaseUrl = "https://gh6famkgs8.execute-api.us-east-2.amazonaws.com/dev";

const policyDocuments = [
    {
        id: "intellicharge",
        shortTitle: "IntelliCharge\u00AE",
        title: null,
        lastUpdated: null,
        html: null,
        status: "loading",
    },
    {
        id: "midaas-platform",
        shortTitle: "MIDAAS Platform",
        title: "Privacy Policy for Grid Elevated\u00AE MIDAAS Platform",
        lastUpdated: "10/16/2024",
        sections: [
            {
                title: "1. Introduction",
                body: [
                    'Welcome to the Grid Elevated\u00AE MIDAAS platform ("we," "our," or "us"). We are committed to protecting your privacy and providing a safe, optimized data management experience. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.'
                ]
            },
            {
                title: "2. Information We Collect",
                body: [
                    "We collect the following types of information:",
                    "- Account Information: Personal details necessary for account creation and management.",
                    "- Uploaded Files: Files uploaded for cloud storage by the user.",
                    "- Functional Module Results: Results of functional modules executed by the user in the platform."
                ]
            },
            {
                title: "3. How We Use Your Information",
                body: [
                    "We use the collected information for the following purposes:",
                    "- To display information to verified users in dashboard views.",
                    "- To improve the performance of functional modules.",
                    "- To create and manage your account."
                ]
            },
            {
                title: "4. Data Security",
                body: [
                    "We implement industry-standard security measures to protect your information:",
                    "- All account information is securely stored by Grid Elevated\u00AE.",
                    "- No third-party access is enabled for your personal data."
                ]
            },
            {
                title: "5. Data Sharing and Disclosure",
                body: ["We do not share your personal information with third parties."]
            },
            {
                title: "6. Your Privacy Rights",
                body: [
                    "You have the right to:",
                    "- Access, correct, or delete your personal information.",
                    "- Opt-out of certain data collection or use.",
                    "For any privacy-related requests, please contact us at info@gridelevated.com."
                ]
            },
            {
                title: "7. Changes to This Privacy Policy",
                body: [
                    'We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.'
                ]
            },
            {
                title: "8. Contact Us",
                body: [
                    "If you have any questions about this Privacy Policy, please contact us at:",
                    "info@gridelevated.com."
                ]
            }
        ]
    }
];

const contactEmail = "info@gridelevated.com";

function formatPolicyDate(value) {
    if (!value) return null;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    return new Intl.DateTimeFormat("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        timeZone: "America/Denver",
    }).format(date);
}

function sanitizeConfluenceHtml(html) {
    const parser = new DOMParser();
    const document = parser.parseFromString(html, "text/html");
    const allowedTags = new Set([
        "A",
        "B",
        "BLOCKQUOTE",
        "BR",
        "EM",
        "H2",
        "H3",
        "H4",
        "HR",
        "I",
        "LI",
        "OL",
        "P",
        "STRONG",
        "TABLE",
        "TBODY",
        "TD",
        "TH",
        "THEAD",
        "TR",
        "UL",
    ]);

    Array.from(document.body.querySelectorAll("*")).forEach((element) => {
        if (!allowedTags.has(element.tagName)) {
            element.replaceWith(...element.childNodes);
            return;
        }

        Array.from(element.attributes).forEach((attribute) => {
            if (element.tagName !== "A" || attribute.name !== "href") {
                element.removeAttribute(attribute.name);
            }
        });

        if (element.tagName === "A") {
            const href = element.getAttribute("href") || "";
            if (!/^(https?:|mailto:|#)/i.test(href)) {
                element.removeAttribute("href");
            }
            element.setAttribute("rel", "noopener noreferrer");
        }
    });

    return document.body.innerHTML;
}

function LinkedText({ text }) {
    if (!text.includes(contactEmail)) {
        return text;
    }

    const [beforeEmail, afterEmail] = text.split(contactEmail);

    return (
        <>
            {beforeEmail}
            <a className="font-semibold text-primaryAccent hover:text-secondary" href={`mailto:${contactEmail}`}>
                {contactEmail}
            </a>
            {afterEmail}
        </>
    );
}

function PolicyBody({ body }) {
    const blocks = body.reduce((formattedBlocks, item) => {
        if (item.startsWith("- ")) {
            const previousBlock = formattedBlocks[formattedBlocks.length - 1];
            const bulletText = item.slice(2);

            if (previousBlock?.type === "list") {
                previousBlock.items.push(bulletText);
            } else {
                formattedBlocks.push({ type: "list", items: [bulletText] });
            }

            return formattedBlocks;
        }

        formattedBlocks.push({ type: "paragraph", text: item });
        return formattedBlocks;
    }, []);

    return (
        <div className="mt-4 space-y-4">
            {blocks.map((block, index) => {
                if (block.type === "list") {
                    return (
                        <ul key={`list-${index}`} className="ml-1 list-disc space-y-2 pl-5">
                            {block.items.map((item) => (
                                <li key={item} className="font-ralewayReg text-base leading-7 text-black/70 marker:text-secondary">
                                    {item}
                                </li>
                            ))}
                        </ul>
                    );
                }

                return (
                    <p key={block.text} className="font-ralewayReg text-base leading-7 text-black/70">
                        <LinkedText text={block.text} />
                    </p>
                );
            })}
        </div>
    );
}

function Privacy() {
    const [documents, setDocuments] = useState(policyDocuments);

    useEffect(() => {
        let cancelled = false;

        fetch(`${apiBaseUrl}/v2/privacy-policy/intellicharge`)
            .then((response) => {
                if (!response.ok) throw new Error("Privacy policy request failed");
                return response.json();
            })
            .then((policy) => {
                if (cancelled || typeof policy.html !== "string" || !policy.html.trim()) {
                    return;
                }

                const sanitizedHtml = sanitizeConfluenceHtml(policy.html);
                if (!sanitizedHtml.trim()) return;

                setDocuments((currentDocuments) =>
                    currentDocuments.map((document) =>
                        document.id === "intellicharge"
                            ? {
                                ...document,
                                title: policy.title,
                                html: sanitizedHtml,
                                lastUpdated: formatPolicyDate(policy.updatedAt),
                                status: "loaded",
                            }
                            : document
                    )
                );
            })
            .catch((error) => {
                console.error("Could not load IntelliCharge privacy policy:", error);
                if (cancelled) return;

                setDocuments((currentDocuments) =>
                    currentDocuments.map((document) =>
                        document.id === "intellicharge"
                            ? { ...document, status: "error" }
                            : document
                    )
                );
            });

        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div className="min-h-screen bg-baseWhite text-baseGray">
            <section className="bg-baseGray px-6 pb-20 pt-32 md:px-10 laptop:px-16 large-laptop:px-24 desktop:px-42">
                <div className="mx-auto max-w-5xl text-center">
                    <p className="font-blender text-base uppercase tracking-[0.35em] text-secondaryAccent md:text-lg">
                        Privacy
                    </p>
                    <h1 className="mx-auto mt-4 max-w-4xl font-blender text-[42px] leading-[44px] text-white md:text-[60px] md:leading-[62px]">
                        Grid Elevated Privacy Policy
                    </h1>
                    <p className="mx-auto mt-5 max-w-3xl font-ralewayReg text-lg leading-8 text-white/85">
                        Privacy policy information for IntelliCharge® and the MIDAAS platform.
                    </p>
                    <div className="mx-auto mt-8 flex max-w-2xl flex-col gap-3 text-sm font-ralewayReg text-white/80 mobile:flex-row mobile:justify-center">
                        {documents.map((document) => (
                            <span key={document.id} className="rounded-md border border-white/15 bg-white/10 px-4 py-2">
                                {document.shortTitle}: {document.lastUpdated || (document.status === "error" ? "Unavailable" : "Loading...")}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            <main className="px-6 pb-16 md:px-10 laptop:px-16 large-laptop:px-24 desktop:px-42">
                <div className="mx-auto -mt-8 grid max-w-6xl gap-8 laptop:grid-cols-[260px_minmax(0,1fr)]">
                    <nav className="rounded-md border border-black/10 bg-white p-5 shadow-sm laptop:hidden" aria-label="Policy documents">
                        <p className="font-blender text-2xl text-baseGray">Policy Documents</p>
                        <div className="mt-4 grid gap-3 mobile:grid-cols-2">
                            {documents.map((document) => (
                                <a key={document.id} className="rounded-md border border-black/10 px-4 py-3 font-ralewayReg text-sm leading-5 text-black/65 hover:border-secondaryAccent hover:text-baseGray" href={`#${document.id}`}>
                                    {document.shortTitle}
                                </a>
                            ))}
                        </div>
                    </nav>

                    <aside className="hidden laptop:block">
                        <nav className="sticky top-28 rounded-md border border-black/10 bg-white p-5 shadow-sm" aria-label="Policy documents">
                            <p className="font-blender text-2xl text-baseGray">Policy Documents</p>
                            <div className="mt-4 space-y-3">
                                {documents.map((document) => (
                                    <a key={document.id} className="block rounded-md border border-transparent px-3 py-3 font-ralewayReg text-sm leading-5 text-black/60 hover:border-secondaryAccent/40 hover:bg-secondaryAccent/10 hover:text-baseGray" href={`#${document.id}`}>
                                        {document.shortTitle}
                                    </a>
                                ))}
                            </div>
                        </nav>
                    </aside>

                    <div className="space-y-8">
                        {documents.map((document) => (
                            <article id={document.id} key={document.title} className="scroll-mt-28 rounded-md border border-black/10 bg-white p-6 shadow-sm md:p-9">
                                <div className="border-b border-black/10 pb-6">
                                    <p className="font-blender text-lg uppercase tracking-[0.2em] text-secondary">
                                        {document.shortTitle}
                                    </p>
                                    {document.title && (
                                        <h2 className="mt-2 font-blender text-3xl leading-9 text-baseGray md:text-4xl md:leading-10">
                                            {document.title}
                                        </h2>
                                    )}
                                    {document.lastUpdated && (
                                        <p className="mt-3 font-ralewayReg text-sm font-semibold uppercase tracking-[0.16em] text-black/50">
                                            Last updated: {document.lastUpdated}
                                        </p>
                                    )}
                                </div>

                                {document.html ? (
                                    <div
                                        className="confluence-policy mt-7"
                                        dangerouslySetInnerHTML={{ __html: document.html }}
                                    />
                                ) : document.status === "loading" ? (
                                    <p className="mt-7 font-ralewayReg text-base leading-7 text-black/70">
                                        Loading the IntelliCharge privacy policy...
                                    </p>
                                ) : document.status === "error" ? (
                                    <div className="mt-7 rounded-md border border-red-200 bg-red-50 p-4">
                                        <p className="font-ralewayReg text-base leading-7 text-red-800">
                                            The IntelliCharge privacy policy could not be loaded. Please try again later.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="mt-7 space-y-7">
                                        {document.sections.map((section) => (
                                            <section key={`${document.title}-${section.title}`} className="border-b border-black/10 pb-7 last:border-b-0 last:pb-0">
                                                <h3 className="font-blender text-2xl leading-7 text-baseGray">
                                                    {section.title}
                                                </h3>
                                                <PolicyBody body={section.body} />
                                            </section>
                                        ))}
                                    </div>
                                )}
                            </article>
                        ))}
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
}

export default Privacy;
